/**
 * Storage utilities for cart data
 * Handles localStorage operations with error handling
 *
 * The cart lives in localStorage: shared across tabs and survives browser
 * restarts (sessionStorage was per tab, which lost the cart in new tabs).
 * Carts written to sessionStorage by older bundles are migrated on first read.
 */

// Key used before the storage key became tenant-unique (cartItems_<merchantId>)
const LEGACY_SHARED_KEY = 'cartItems';

/**
 * Get items from storage
 * @param {string} storageKey - The storage key to use
 * @returns {Array} Array of stored items or empty array if error
 */
export function getStorage(storageKey = 'cartItems') {
    try {
        const data = localStorage.getItem(storageKey);
        if (data !== null) {
            return JSON.parse(data);
        }
        return migrateFromSessionStorage(storageKey);
    } catch (error) {
        console.error('Error reading from storage:', error);
        return [];
    }
}

/**
 * One-time migration of a sessionStorage cart (older bundle) into localStorage.
 * Checks both the current key and the legacy shared key.
 * @param {string} storageKey - The target localStorage key
 * @returns {Array} Migrated items or empty array
 */
function migrateFromSessionStorage(storageKey) {
    const candidateKeys = storageKey === LEGACY_SHARED_KEY
        ? [LEGACY_SHARED_KEY]
        : [storageKey, LEGACY_SHARED_KEY];

    for (const key of candidateKeys) {
        let legacy = null;
        try {
            legacy = sessionStorage.getItem(key);
        } catch (error) {
            return [];
        }
        if (legacy === null) continue;

        try {
            const items = JSON.parse(legacy);
            localStorage.setItem(storageKey, legacy);
            sessionStorage.removeItem(key);
            console.log('Storage: Migrated cart from sessionStorage to localStorage');
            return items;
        } catch (error) {
            console.error('Error migrating cart from sessionStorage:', error);
            return [];
        }
    }
    return [];
}

/**
 * Set items in storage
 * @param {string} storageKey - The storage key to use
 * @param {Array} items - Items to store
 * @returns {boolean} Success status
 */
export function setStorage(storageKey, items) {
    try {
        localStorage.setItem(storageKey, JSON.stringify(items));
        return true;
    } catch (error) {
        console.error('Error writing to storage:', error);
        return false;
    }
}

/**
 * Clear storage
 * @param {string} storageKey - The storage key to clear
 * @returns {boolean} Success status
 */
export function clearStorage(storageKey = 'cartItems') {
    try {
        localStorage.removeItem(storageKey);
        return true;
    } catch (error) {
        console.error('Error clearing storage:', error);
        return false;
    }
}
